const isProduction = process.env.NODE_ENV === "production";

const logAnalytics = (functionName, event, properties) => {
  if (!isProduction) {
    console.info(`analytics.js :: ${functionName} :: `, event, properties);
  }
};

export const trackEvent = (event, properties) => {
  logAnalytics("trackEvent", event, properties);

  window?.gtag && window.gtag("event", event, properties);
  window?.posthog && window.posthog.capture(event, properties);
};

export const identify = (properties) => {
  logAnalytics("identify", properties, null);

  if (window?.gtag) {
    Object.entries(properties).forEach(([key, value]) => {
      window.gtag("config", process.env.GATSBY_GA4_MEASUREMENT_ID, {
        send_page_view: false,
        user_properties: {
          [key]: value,
        },
      });
    });
  }

  if (window?.posthog) {
    const phDistinctId = window.posthog?.get_distinct_id?.();
    phDistinctId && window.posthog.identify(phDistinctId, properties);
  }
};

let lastPageView = { path: "", timestamp: 0 };

export const trackPageView = (location) => {
  const currentPath = location.pathname + location.search + location.hash;
  const now = Date.now();

  // Prevent duplicate page views within 500ms for the same path
  if (lastPageView.path === currentPath && now - lastPageView.timestamp < 500) {
    return;
  }

  lastPageView = { path: currentPath, timestamp: now };
  logAnalytics("trackPageView", location.pathname, null);

  window?.gtag &&
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_path: location.pathname + location.search + location.hash,
    });

  window.wisepops && window.wisepops("pageview");

  // OpenAI drops the event if name is an empty (or whitespace-only) string, and
  // document.title can be "" or stale before React Helmet updates it on client-side
  // nav. Fall back to the pathname, which is always present and non-empty.
  const oaiqName = document.title.trim() || location.pathname;

  window?.oaiq &&
    window.oaiq("measure", "page_viewed", {
      type: "contents",
      contents: [{ id: location.pathname, name: oaiqName, content_type: "page" }],
    });
};

export const trackTikTokConversion = (conversionType, { email, phone } = {}) => {
  logAnalytics("trackTikTokConversion", conversionType, null);

  if (!window?.ttq) return;

  // Plaintext: the pixel hashes it. Pre-hashed values need the sha256_ prefixed keys.
  const ids = {};
  const cleanEmail = email?.trim().toLowerCase();
  if (cleanEmail) ids.email = cleanEmail;

  const digits = phone?.replace(/\D/g, "");
  if (digits) ids.phone_number = digits.length === 10 ? `+1${digits}` : `+${digits}`;

  // identify must precede track for the event to pick up the match data
  if (Object.keys(ids).length) window.ttq.identify(ids);

  // TikTok rejects custom events as optimization goals, so trials and demos share
  // CompleteRegistration as a combined signal. content_name keeps them apart in reporting.
  window.ttq.track("CompleteRegistration", { content_name: conversionType });
};

export const trackOpenAIConversion = (conversionType) => {
  const conversionEvents = {
    demo: { event: "registration_completed", type: "customer_action" },
    trial: { event: "trial_started", type: "plan_enrollment" },
  };

  const conversion = conversionEvents[conversionType];
  if (!conversion) {
    console.warn(
      `Unknown OpenAI conversion type: ${conversionType}. Valid types: ${Object.keys(conversionEvents).join(", ")}`
    );
    return;
  }

  logAnalytics("trackOpenAIConversion", conversionType, conversion);

  if (window?.oaiq) {
    window.oaiq("measure", conversion.event, { type: conversion.type });
    // ChatGPT Ads optimizes on one standard event per campaign and rejects custom
    // events as goals, so trials and demos share lead_created as a combined signal.
    window.oaiq("measure", "lead_created", { type: "customer_action" });
  }
};

export const trackMetaConversion = (conversionType) => {
  const conversionEvents = {
    demo: "Lead",
    trial: "CompleteRegistration",
  };

  const event = conversionEvents[conversionType];
  if (!event) {
    console.warn(
      `Unknown Meta conversion type: ${conversionType}. Valid types: ${Object.keys(conversionEvents).join(", ")}`
    );
    return;
  }

  logAnalytics("trackMetaConversion", conversionType, { event });

  // fbq is absent on pages where the Meta Pixel is skipped (e.g. /meet/ links with a phone param)
  if (window?.fbq) {
    window.fbq("track", event);
    // Trials and demos also fire SubmitApplication so ad sets can optimize against
    // the combined volume. Lead and CompleteRegistration still fire for reporting.
    window.fbq("track", "SubmitApplication");
  }
};

export const trackMetaCustomEvent = (event, properties = {}) => {
  logAnalytics("trackMetaCustomEvent", event, properties);

  // fbq is absent on pages where the Meta Pixel is skipped (e.g. /meet/ links with a phone param)
  window?.fbq && window.fbq("trackCustom", event, properties);
};

export const trackBingConversion = (event, properties = {}) => {
  logAnalytics("trackBingConversion", event, properties);

  window.uetq = window.uetq || [];
  window.uetq.push("event", event, properties);
};

export const trackGoogleAdsConversion = (conversionType) => {
  const conversionLabels = {
    demo: "CJLKCOGytaEBEOGPv_oC",
    trial: "9I8oCK3QwscBEOGPv_oC",
  };

  const conversionLabel = conversionLabels[conversionType];
  if (!conversionLabel) {
    console.warn(
      `Unknown conversion type: ${conversionType}. Valid types: ${Object.keys(conversionLabels).join(", ")}`
    );
    return;
  }

  logAnalytics("trackGoogleAdsConversion", conversionType, { label: conversionLabel });

  if (window?.gtag) {
    window.gtag("event", "conversion", {
      send_to: `${process.env.GATSBY_GOOGLE_ADS_ID}/${conversionLabel}`,
    });
  }
};

export const trackGartnerCapterraConversion = (conversionType) => {
  const conversionIds = {
    demo: "552c577d-205d-4ec8-8fc3-ed788b994eca",
    trial: "2d8a54cb-eb96-47d2-b428-85eeba94e3c0",
  };

  const installationId = conversionIds[conversionType];
  if (!installationId) {
    console.warn(
      `Unknown Capterra conversion type: ${conversionType}. Valid types: ${Object.keys(conversionIds).join(", ")}`
    );
    return;
  }

  logAnalytics("trackGartnerCapterraConversion", conversionType, { installationId });

  if (window?.ct?.sendEvent) {
    window.ct.sendEvent({ installationId });
  }
};
